<script setup lang="ts">
// Vue APIs
import { onBeforeMount, ref, watch, onMounted } from 'vue'
import { RouterView } from 'vue-router'

// Third-party
import { useI18n } from 'vue-i18n'

// Local imports
import { useAuth } from '@/stores/auth'
import { useSidebar } from './stores/sidebar'
import { useDarkMode } from '@/composables/useDarkMode'
import { useLoading } from '@/composables/useLoading'
import LoginView from '@/views/public/LoginView.vue'
import SideBar from '@/components/layouts/SideBar.vue'
import NavBar from '@/components/layouts/NavBar.vue'

// vars ----------------------------------------
const storeSideBar = useSidebar('main')()
const auth = useAuth()
const isMobile = ref(false)
const { t } = useI18n()
const isToggleSideBar = ref(false)
const error = ref<string | null>(null)
const { BASE_COLOR } = CONFIG
const { isLoading } = useLoading()

// Initialize dark mode early (before mount)
useDarkMode()

// lifecycle hooks ----------------------------------------
onBeforeMount(async () => {
    try {
    await auth.checkLogin()
    if (auth.isLoggedIn) {
      await storeSideBar.onLogin()
    }
  } catch (err) {
  error.value = err instanceof Error ? err.message : 'Unknown error'
  } finally {
    auth.loading = false
  }
})

onMounted(() => {
  checkDevice()
  window.addEventListener('resize', checkDevice)
})


// methods ----------------------------------------
const checkDevice = () : void => {
  isMobile.value = window.innerWidth < 1024
}

const toggleSideBar = (value: boolean) : void => {
  isToggleSideBar.value = value
}

// watch ----------------------------------------
watch(() => auth.isLoggedIn, async (loggedIn) => {
  try {
    if (loggedIn) {
      await storeSideBar.onLogin()
    } else {
      storeSideBar.onLogout()
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Unknown error'
  }
})

</script>

<template>

  <!-- loading view -->
  <div v-if="auth.loading" class="flex justify-center items-center min-h-screen">
    <div class="text-xl flex flex-col items-center justify-center gap-4">
      <span class="loading loading-bars loading-lg" :style="{ color: BASE_COLOR }"/>
      <h1 class="text-[20px] leading-[27px] text-font-dark">
        {{ t('loading') }}
      </h1>
    </div>
  </div>

  <transition v-else name="fade">
    <!-- login view -->
    <LoginView v-if="!auth.isLoggedIn" />
    <!-- main content -->
    <div v-else class="flex w-full min-h-screen flex-nowrap">
      <!-- sidebar -->
      <SideBar :is-mobile="isMobile" :is-toggle-side-bar="isToggleSideBar" @toggleSideBar="toggleSideBar" />
      <!-- mobile sidebar -->
      <div v-if="isMobile && isToggleSideBar" class="fixed inset-0 bg-black/40 z-10 lg:hidden"
        @click="toggleSideBar(false)"></div>
      <!-- main content -->
      <div class="flex flex-col motion-safe:transition-all motion-safe:duration-normal grow overflow-x-hidden overflow-y-auto" :class="[
        storeSideBar.collapsed
          ? 'lg:ps-[100px]'
          : 'lg:ps-[210px] 2xl:ps-[300px]'
]">
        <!-- api loading bar -->
        <div v-if="isLoading" class="fixed top-0 inset-x-0 z-50 h-1 overflow-hidden">
          <div class="loading-bar h-full w-2/5" :style="{ backgroundColor: BASE_COLOR }"/>
        </div>
        <!-- navbar -->
        <NavBar :is-mobile="isMobile" @toggleSideBar="toggleSideBar" />
        <!-- main content -->
        <RouterView />
      </div>
    </div>
  </transition>
</template>



<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loading-bar {
  animation: loading-slide 1.5s ease-in-out infinite;
}

@keyframes loading-slide {
  0% {
    transform: translateX(-150%);
  }

  100% {
    transform: translateX(550%);
  }
}
</style>
